import React, { useState, useCallback, useEffect, useRef } from 'react';
import Cropper from 'react-easy-crop';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Slider,
  Typography,
  CircularProgress,
} from '@mui/material';
import { getCroppedImageBlob } from '../utils/canvasCropImage';
import {
  BANNER_EXPORT_WIDTH,
  BANNER_EXPORT_HEIGHT,
  PROFILE_EXPORT_WIDTH,
  PROFILE_EXPORT_HEIGHT,
} from '../utils/bannerStripStyles';

/** Re-export for any code that imported these from here */
export const BANNER_CROP_WIDTH = BANNER_EXPORT_WIDTH;
export const BANNER_CROP_HEIGHT = BANNER_EXPORT_HEIGHT;
const BANNER_ASPECT = BANNER_EXPORT_WIDTH / BANNER_EXPORT_HEIGHT;
const PROFILE_ASPECT = 1;
/** Below 1 = zoom out (needed for square images in a wide frame). */
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 3;

/**
 * @param {{ open: boolean, imageSrc: string, onClose: () => void, onApply: (file: File) => Promise<void>, variant?: 'banner' | 'profile' }} props
 */
export default function BannerCropDialog({ open, imageSrc, onClose, onApply, variant = 'banner' }) {
  const isProfile = variant === 'profile';
  const exportW = isProfile ? PROFILE_EXPORT_WIDTH : BANNER_EXPORT_WIDTH;
  const exportH = isProfile ? PROFILE_EXPORT_HEIGHT : BANNER_EXPORT_HEIGHT;
  const aspect = isProfile ? PROFILE_ASPECT : BANNER_ASPECT;
  const dialogTitle = isProfile ? 'Adjust profile picture' : 'Adjust banner';
  const outputFileName = isProfile ? 'profile.jpg' : 'banner.jpg';

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const croppedAreaPixelsRef = useRef(null);
  /** Class instance exposes getCropData() — use on Apply so pixels match the preview (avoids stale callbacks). */
  const cropperInstanceRef = useRef(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      croppedAreaPixelsRef.current = null;
      setApplying(false);
    }
  }, [open]);

  /**
   * react-easy-crop calls onCropComplete from emitCropData, but crop-only updates
   * (drag / follow-up after zoom) often go through onCropAreaChange only — both
   * must update pixels or the saved image mismatches the preview.
   */
  const onCropPixelsUpdate = useCallback((_area, areaPixels) => {
    croppedAreaPixelsRef.current = areaPixels;
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleApply = async () => {
    if (!imageSrc) return;
    setApplying(true);
    try {
      await new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });
      const fromInstance = cropperInstanceRef.current?.getCropData?.()?.croppedAreaPixels;
      const pixels = fromInstance ?? croppedAreaPixelsRef.current ?? croppedAreaPixels;
      if (!pixels) return;
      const blob = await getCroppedImageBlob(imageSrc, pixels, {
        outputWidth: exportW,
        outputHeight: exportH,
      });
      const file = new File([blob], outputFileName, { type: 'image/jpeg' });
      await onApply(file);
      onClose();
    } catch (e) {
      console.error('Image crop failed:', e);
      alert('Could not process the image. Try another file.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onClose={applying ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {isProfile ? (
            <>
              Frame <strong>{PROFILE_EXPORT_WIDTH}×{PROFILE_EXPORT_HEIGHT}px</strong> (square). Drag to reposition. Move zoom <strong>left</strong> to zoom out; right to zoom in. Saved file matches this size.
            </>
          ) : (
            <>
              Frame <strong>{BANNER_EXPORT_WIDTH}×{BANNER_EXPORT_HEIGHT}px</strong>. Drag to reposition. Move zoom <strong>left</strong> to zoom out (helps square logos); right to zoom in. Saved file matches this size.
            </>
          )}
        </Typography>
        {imageSrc ? (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: exportW,
              mx: 'auto',
              aspectRatio: `${exportW} / ${exportH}`,
              bgcolor: '#0f172a',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ position: 'absolute', inset: 0 }}>
              <Cropper
                ref={cropperInstanceRef}
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={0}
                minZoom={MIN_ZOOM}
                maxZoom={MAX_ZOOM}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropPixelsUpdate}
                onCropAreaChange={onCropPixelsUpdate}
                showGrid={false}
              />
            </Box>
          </Box>
        ) : null}
        <Box sx={{ mt: 2, px: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Zoom
          </Typography>
          <Slider
            value={zoom}
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            aria-label="Zoom"
            onChange={(_, v) => setZoom(v)}
            disabled={!imageSrc || applying}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={applying}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleApply} disabled={!croppedAreaPixels || applying}>
          {applying ? <CircularProgress size={22} color="inherit" /> : 'Apply & upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
